import { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import keycloak from '../../config/keycloak';
import { addMessages } from '../slices/message';
import { MESSAGE_ERROR } from '../../constants/message';

export const safeBaseQuery = (baseUrl: string): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> => {
    const baseQuery = fetchBaseQuery({
        baseUrl: `/api/${baseUrl}`,
        prepareHeaders: (headers) => {
            if (keycloak.authenticated && keycloak.token) {
                headers.set('Authorization', `Bearer ${keycloak.token}`);
            }
            return headers;
        },
    });

    return async (args, api, extraOptions) => {
        if (keycloak.authenticated) {
            try {
                await keycloak.updateToken(30);
            } catch {
                keycloak.login();
                return { error: { status: 401, data: 'Token refresh failed' } as FetchBaseQueryError };
            }
        }

        const result = await baseQuery(args, api, extraOptions);

        if (result.error) {
            const error = result.error as FetchBaseQueryError;
            let message;
            if ('status' in error) {
                if (typeof error.data === 'object' && error.data !== null && 'message' in error.data) {
                    message = `Error: ${(error.data as any).message}`;
                } else {
                    message = `Error: ${error.status}`;
                }
            } else {
                message = `Unexpected error: ${JSON.stringify(error)}`;
            }

            if (message) {
                api.dispatch(addMessages([[MESSAGE_ERROR, message]]));
            }
        }

        return result;
    };
};

export function buildPaginatedQuery<TFilters extends Record<string, any>>(args: {
    filters?: TFilters;
    page?: number;
    pageSize?: number;
    orderBy?: string;
    asc?: boolean;
}) {
    const { filters, page, pageSize, orderBy, asc } = args;
    const params = new URLSearchParams();

    if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                if (Array.isArray(value)) {
                    value.forEach((v) => params.append(key, String(v)));
                } else {
                    params.append(key, String(value));
                }
            }
        });
    }

    if (page) params.append('page', page.toString());
    if (pageSize) params.append('pageSize', pageSize.toString());
    if (orderBy) params.append('orderBy', orderBy);
    if (asc !== undefined) params.append('asc', String(asc));

    return {
        url: ``,
        params,
    };
}
